/**
 * 서비스 관리 상태 및 데이터 페칭 Hook
 */
import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminAPI } from '@/services/api';
import { useServices, useServiceActions } from '@/hooks/services';
import { useNotification } from '@/hooks/common';
import { MSAService } from '@/types';

export const useServiceManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const subtabFromUrl = searchParams.get('subtab');
  // suspended가 URL에서 오면 approved로 리다이렉트
  const normalizedSubtab = (subtabFromUrl === 'suspended' ? 'approved' : subtabFromUrl) as 'pending' | 'approved' | 'all' | 'deleted_rejected' | null;
  const [activeTab, setActiveTab] = useState<'all' | 'approved' | 'pending' | 'deleted_rejected'>(
    (normalizedSubtab as any) || 'all'
  );
  const { success, setSuccess } = useNotification({ duration: 3000 });
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const [selectedService, setSelectedService] = useState<MSAService | null>(null);
  const [showServiceDetail, setShowServiceDetail] = useState(false);
  const [showQuotaPlanManagement, setShowQuotaPlanManagement] = useState(false);
  const [serviceCounts, setServiceCounts] = useState<Record<string, number>>({
    all: 0,
    approved: 0,
    suspended: 0,
    pending: 0,
    deleted: 0,
    rejected: 0,
    deleted_rejected: 0,
  });

  // activeTab에 따라 statusFilter 결정
  // approved 탭은 approved + suspended를 포함하므로 undefined로 모든 서비스 가져오기
  const statusFilter = activeTab === 'all' || activeTab === 'approved'
    ? undefined
    : activeTab === 'deleted_rejected'
    ? 'deleted_rejected' // 백엔드에서 처리
    : activeTab;
  
  const {
    services,
    loading,
    error,
    setError,
    healthStatuses,
    fetchServices,
  } = useServices(statusFilter);

  // 서비스 카운트 가져오기
  const fetchServiceCounts = useCallback(async () => {
    try {
      const counts = await adminAPI.getServiceCounts();
      setServiceCounts(counts);
    } catch (err) {
      console.error('Failed to fetch service counts:', err);
    }
  }, []);

  // 초기 로드 시 카운트 가져오기
  useEffect(() => {
    fetchServiceCounts();
  }, [fetchServiceCounts]);

  // activeTab이 변경될 때마다 서비스 다시 가져오기
  useEffect(() => {
    fetchServices();
  }, [activeTab, fetchServices]);

  // 서비스가 변경될 때마다 카운트 업데이트
  useEffect(() => {
    fetchServiceCounts();
  }, [services, fetchServiceCounts]);

  const handleRefresh = useCallback(async () => {
    await fetchServices();
  }, [fetchServices]);

  const serviceActions = useServiceActions(handleRefresh, services);

  const handleSubTabChange = (tab: string) => {
    setActiveTab(tab as any);
    setSearchParams({ subtab: tab });
  };

  const handleServiceClick = useCallback((service: MSAService) => {
    setSelectedService(service);
    setShowServiceDetail(true);
  }, []);

  const handleManagePlans = useCallback((service: MSAService) => {
    setSelectedService(service);
    setShowQuotaPlanManagement(true);
  }, []);

  return {
    // State
    activeTab,
    selectedServiceId,
    selectedService,
    showServiceDetail,
    showQuotaPlanManagement,
    serviceCounts,
    services,
    loading,
    error,
    healthStatuses,
    success,
    // Actions
    setActiveTab,
    setSelectedServiceId,
    setSelectedService,
    setShowServiceDetail,
    setShowQuotaPlanManagement,
    setError,
    setSuccess,
    handleSubTabChange,
    handleRefresh,
    handleServiceClick,
    handleManagePlans,
    fetchServices,
    fetchServiceCounts,
    serviceActions,
  };
};
